import { useContext, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAsyncEffect from 'use-async-effect';
import classNames from 'classnames';
import { SlipContext } from 'context/slipContext';
import { PrematchCatalogContext } from 'context/prematchCatalogContext';
import {
  getEventsByLeague,
  getHighlights,
  getLastMinuteEvents,
  getLeagues,
} from 'services/api/sportBetsService';
import { MatchOffer } from 'types/MatchOffer';
import type { League } from 'types/League';
import { SportIDs } from 'types/enums';
import { getFullTimeOdds } from 'helpers/matchHelper';
import { getDefaultSportIconSrc } from 'helpers/sportIcon';
import { formatDateTime } from 'helpers/dateHelper';
import { SportIcon } from 'components/ThemeIcon/SportIcon';
import { CountryFlag, TeamCrest } from 'components/TeamCrest/TeamCrest';
import { ThemeMenuIcon } from 'components/ThemeMenuIcon/ThemeMenuIcon';
import { ModernSportNav } from 'components/mobile/ModernSportNav/ModernSportNav';
import { BetTogether } from './BetTogether';
import { PopularSlips } from './PopularSlips';
import { TopGames } from './TopGames';
import { LuckySixHome } from './LuckySixHome';
import { KenoHome } from './KenoHome';
import { RailArrows } from './RailArrows';
import styles from './ModernHome.module.css';

type Source = 'top' | 'lastMinute';
type View = 'cards' | 'list';

const VIEW_KEY = 'tottalo.modernHomeView';

function readView(): View {
  try {
    return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'cards';
  } catch {
    return 'cards';
  }
}

function saveView(v: View) {
  try {
    localStorage.setItem(VIEW_KEY, v);
  } catch {
    // Private mode: the choice lasts for this visit.
  }
}

/** The 1 X 2 buttons of a match, shared by the card and the list row. */
function OddButtons({ match }: { match: MatchOffer }) {
  const { t } = useTranslation();
  const { addSlipEntry, removeSlipEntry, entryExist } = useContext(SlipContext);
  const { homeWin, draw, awayWin } = getFullTimeOdds(match);
  const picks = [
    { key: '1', label: t('bet_col_1x2_home'), odd: homeWin },
    { key: 'X', label: t('bet_col_1x2_draw'), odd: draw },
    { key: '2', label: t('bet_col_1x2_away'), odd: awayWin },
  ].filter((p) => p.odd);

  return (
    <div className={styles.eventOdds}>
      {picks.map((p) => {
        const selected = entryExist(match, match.fullTimeBet, p.odd);
        return (
          <button
            type="button"
            key={p.key}
            className={classNames(styles.odd, selected && styles.oddSelected)}
            onClick={() =>
              selected
                ? removeSlipEntry(match, match.fullTimeBet, p.odd)
                : addSlipEntry(match, match.fullTimeBet, p.odd)
            }
          >
            <span>{p.label}</span>
            <strong>{p.odd.odds.toFixed(2)}</strong>
          </button>
        );
      })}
    </div>
  );
}

/** League line with sport icon and country flag. */
function LeagueLine({ match }: { match: MatchOffer }) {
  return (
    <>
      <SportIcon
        sportId={match.league.sport}
        className={styles.eventSport}
        onError={(e) => {
          e.currentTarget.onerror = null;
          e.currentTarget.src = getDefaultSportIconSrc();
        }}
      />
      <CountryFlag cc={match.league.cc} className={styles.eventFlag} />
      <span className={styles.eventLeague}>{match.league.name}</span>
    </>
  );
}

/** One event card of the sideways rail: league, both teams, kick-off and
 *  1 X 2. */
function TopEventCard({ match }: { match: MatchOffer }) {
  const navigate = useNavigate();
  const open = () => navigate(`/sports-betting/match/${match.fi}`);

  return (
    <div className={styles.eventCard}>
      <button type="button" className={styles.eventHead} onClick={open}>
        <LeagueLine match={match} />
        <span className={styles.chev}>›</span>
      </button>
      <button type="button" className={styles.eventTeams} onClick={open}>
        <span className={styles.team}>
          <TeamCrest
            imageId={match.home?.image_id}
            size="m"
            className={styles.crest}
          />
          <span>{match.home?.name}</span>
        </span>
        <span className={styles.kickoff}>
          {formatDateTime(new Date(match.time * 1000), {
            removeSeconds: true,
            removeYear: true,
          })}
        </span>
        <span className={styles.team}>
          <TeamCrest
            imageId={match.away?.image_id}
            size="m"
            className={styles.crest}
          />
          <span>{match.away?.name}</span>
        </span>
      </button>
      <OddButtons match={match} />
    </div>
  );
}

/** The same event as a list row: league, kick-off time, both teams with
 *  their crests, and 1 X 2 underneath. */
function EventRow({ match }: { match: MatchOffer }) {
  const navigate = useNavigate();
  const open = () => navigate(`/sports-betting/match/${match.fi}`);

  return (
    <div className={styles.row}>
      <button type="button" className={styles.eventHead} onClick={open}>
        <LeagueLine match={match} />
      </button>
      <button type="button" className={styles.rowMain} onClick={open}>
        <span className={styles.rowTime}>
          {formatDateTime(new Date(match.time * 1000), {
            removeDate: true,
            removeSeconds: true,
          })}
        </span>
        <span className={styles.rowTeams}>
          <span>
            <TeamCrest
              imageId={match.home?.image_id}
              className={styles.rowCrest}
            />
            {match.home?.name}
          </span>
          <span>
            <TeamCrest
              imageId={match.away?.image_id}
              className={styles.rowCrest}
            />
            {match.away?.name}
          </span>
        </span>
        <span className={styles.chev}>›</span>
      </button>
      <OddButtons match={match} />
    </div>
  );
}

/** The list in kick-off order, under a heading per day. */
function EventList({ matches }: { matches: MatchOffer[] }) {
  const days: { day: string; matches: MatchOffer[] }[] = [];
  [...matches]
    .sort((a, b) => a.time - b.time)
    .forEach((m) => {
      const day = formatDateTime(new Date(m.time * 1000), { removeTime: true });
      const last = days[days.length - 1];
      if (last && last.day === day) last.matches.push(m);
      else days.push({ day, matches: [m] });
    });
  return (
    <div className={styles.dayList}>
      {days.map((d) => (
        <div key={d.day}>
          <div className={styles.day}>{d.day}</div>
          {d.matches.map((m) => (
            <EventRow key={m.fi} match={m} />
          ))}
        </div>
      ))}
    </div>
  );
}

const StarIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden>
    <path
      fill="currentColor"
      d="M12 2.8l2.8 5.9 6.4.8-4.7 4.4 1.2 6.4L12 17.2l-5.7 3.1 1.2-6.4-4.7-4.4 6.4-.8z"
    />
  </svg>
);

const StopwatchIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden>
    <path
      fill="currentColor"
      d="M9 1.5h6v2H9zM19 7.4l1.4-1.4 1.4 1.4-1.4 1.4A8.5 8.5 0 1 1 12 4.5a8.4 8.4 0 0 1 7 2.9zM12 6.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zm-1 2h2v5h-2z"
    />
  </svg>
);

const CardsIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden>
    <rect x="6" y="4" width="12" height="16" rx="2.5" fill="currentColor" />
    <rect x="1" y="7" width="3.5" height="10" rx="1.5" fill="currentColor" />
    <rect x="19.5" y="7" width="3.5" height="10" rx="1.5" fill="currentColor" />
  </svg>
);

const ListIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden>
    <path
      fill="currentColor"
      d="M3 5h3v3H3zm5 0h13v3H8zM3 10.5h3v3H3zm5 0h13v3H8zM3 16h3v3H3zm5 0h13v3H8z"
    />
  </svg>
);

/** The modern home; `desktop` is the wide page inside the desktop shell: the
 *  shell's sidebar lists the sports, and the rails page with ‹ › buttons. */
export const ModernHome = ({ desktop = false }: { desktop?: boolean }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lastPrematchPush } = useContext(PrematchCatalogContext);
  const bump = lastPrematchPush?.version ?? 0;
  const [sportId, setSportId] = useState<number>(SportIDs.SOCCER);
  const [top, setTop] = useState<MatchOffer[] | null>(null);
  const [lastMinute, setLastMinute] = useState<MatchOffer[] | null>(null);
  const [source, setSource] = useState<Source>('top');
  const [view, setView] = useState<View>(readView);
  const eventRail = useRef<HTMLDivElement>(null);

  // Each list shows as soon as it arrives: the highlights are much the
  // heaviest and must not hold back the sports or last minute.
  useAsyncEffect(
    async (isMounted) => {
      setTop(null);
      // Soccer has picked highlights; other sports show their top league.
      let h: MatchOffer[] | null;
      if (sportId === SportIDs.SOCCER) {
        h = await getHighlights(true);
      } else {
        const leagues: League[] | null = await getLeagues(sportId).catch(
          () => null
        );
        h = leagues?.length
          ? await getEventsByLeague(
              leagues[0].id,
              sportId,
              undefined,
              undefined,
              true
            )
          : [];
      }
      if (isMounted()) setTop(h ?? []);
    },
    [bump, sportId]
  );
  useAsyncEffect(
    async (isMounted) => {
      const l = await getLastMinuteEvents();
      if (isMounted()) setLastMinute(l ?? []);
    },
    [bump]
  );

  const events = source === 'top' ? top : lastMinute;
  const shown = events?.slice(0, view === 'cards' ? 12 : 20);
  const pickView = (v: View) => {
    setView(v);
    saveView(v);
  };

  return (
    <div className={classNames(styles.page, desktop && styles.desktop)}>
      <BetTogether />
      <button
        type="button"
        className={styles.search}
        onClick={() => navigate('/search')}
      >
        <ThemeMenuIcon icon="search" alt="" variant="footer" />
        <span>{t('modern_search_placeholder')}</span>
      </button>

      {!desktop && (
        <ModernSportNav sportId={sportId} onSport={(sp) => setSportId(sp.id)} />
      )}

      <div className={styles.sectionHead}>
        <h2>
          {source === 'top'
            ? t('modern_top_events')
            : t('last_minute_events_label')}
        </h2>
        <div className={styles.toggles}>
          <div className={styles.toggle}>
            <button
              type="button"
              className={classNames(source === 'top' && styles.toggleOn)}
              aria-label={t('modern_top_events')}
              aria-pressed={source === 'top'}
              onClick={() => setSource('top')}
            >
              <StarIcon />
            </button>
            <button
              type="button"
              className={classNames(source === 'lastMinute' && styles.toggleOn)}
              aria-label={t('last_minute_events_label')}
              aria-pressed={source === 'lastMinute'}
              onClick={() => setSource('lastMinute')}
            >
              <StopwatchIcon />
            </button>
          </div>
          <div className={styles.toggle}>
            <button
              type="button"
              className={classNames(view === 'cards' && styles.toggleOn)}
              aria-label={t('modern_view_cards')}
              aria-pressed={view === 'cards'}
              onClick={() => pickView('cards')}
            >
              <CardsIcon />
            </button>
            <button
              type="button"
              className={classNames(view === 'list' && styles.toggleOn)}
              aria-label={t('modern_view_list')}
              aria-pressed={view === 'list'}
              onClick={() => pickView('list')}
            >
              <ListIcon />
            </button>
          </div>
          {desktop && view === 'cards' && <RailArrows rail={eventRail} />}
        </div>
      </div>

      {view === 'cards' ? (
        <div className={styles.eventRail} ref={eventRail}>
          {events === null &&
            [0, 1].map((i) => <div key={i} className={styles.skeleton} />)}
          {shown?.map((m) => <TopEventCard key={m.fi} match={m} />)}
        </div>
      ) : (
        <>
          {events === null && <div className={styles.skeletonRow} />}
          {shown && shown.length > 0 && <EventList matches={shown} />}
        </>
      )}
      {events?.length === 0 && (
        <p className={styles.empty}>{t('no_events_in_period')}</p>
      )}
      {events && events.length > 0 && (
        <button
          type="button"
          className={styles.showMore}
          onClick={() =>
            navigate(
              source === 'lastMinute'
                ? '/sports-betting/last-minute-events'
                : sportId === SportIDs.SOCCER
                  ? '/sports-betting/highlights'
                  : '/sports-betting'
            )
          }
        >
          {t('modern_show_more')} ›
        </button>
      )}

      <TopGames desktop={desktop} />

      <LuckySixHome />

      <KenoHome />

      <PopularSlips bump={bump} desktop={desktop} />
    </div>
  );
};
